import { IconButton, Stack, Tooltip, alpha } from '@mui/material';
import { styled } from '@mui/material/styles';
import cn from 'classnames';
import Iconify from 'src/components/iconify';
import { VoiceChannelMember } from 'src/provider/VoiceChannel';
import { useLivekitRTCState } from 'src/provider/VoiceChannel/livekit/useLivekitRTCState';
import { MemberInfo, MemberName, MemberNameContainer } from './styles';
import { useMemberVariables } from './useMemberVariables';

type Props = {
  member: VoiceChannelMember;
  connected: boolean;
  aloneMember: boolean;
};

export const MemberInfoContent = ({
  member,
  connected,
  aloneMember,
}: Props) => {
  const { theaterMember, setTheaterMember } = useLivekitRTCState();

  const { hasVideo, theater, startedByCurrentMember } = useMemberVariables({
    member,
    connected,
    aloneMember,
  });

  const toggleFullscreen = async (el: HTMLElement) => {
    const isInFullscreen =
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement;

    if (isInFullscreen) {
      await document.exitFullscreen();
      return;
    }

    await el.requestFullscreen();
  };

  const toggleTheaterMode = () => {
    setTheaterMember(
      theaterMember === member?.meetId ? null : (member.meetId as string),
    );
  };

  return (
    <MemberInfo className={cn({ hasVideo: connected && hasVideo, connected })}>
      <MemberNameContainer className="name-container">
        {member.sharing && (
          <Iconify
            className="video-icon"
            icon="material-symbols:screen-share-outline"
            color="success.main"
          />
        )}
        {member.camera && (
          <Iconify
            className="video-icon"
            icon="tabler:video"
            color="success.main"
          />
        )}
        <MemberName className="name">{member.user.name}</MemberName>
        {(member.muted || member.deaf) && (
          <Stack direction="row" alignItems="center" ml={1}>
            {member.muted && (
              <StyledIcon icon="clarity:microphone-mute-solid" />
            )}

            {member.deaf && <StyledIcon icon="mdi:headphones-off" />}
          </Stack>
        )}
      </MemberNameContainer>

      <Stack direction="row" gap={1} className="video-action-buttons">
        {connected && (
          <>
            {(hasVideo || theater) && (
              <StyledIconButton
                size="small"
                edge="end"
                disableTouchRipple
                onClick={toggleTheaterMode}
                className={cn('theaterButton', {
                  theaterMember: theaterMember === member?.meetId,
                })}
              >
                <Iconify
                  icon={
                    theater
                      ? 'tabler:arrows-diagonal-minimize'
                      : 'tabler:arrows-diagonal-2'
                  }
                  color="white"
                />
              </StyledIconButton>
            )}
            {hasVideo && (
              <StyledIconButton
                size="small"
                edge="end"
                disableTouchRipple
                onClick={(e) =>
                  toggleFullscreen(
                    e.currentTarget.parentElement!.parentElement!
                      .parentElement!,
                  )
                }
              >
                <Iconify icon="tabler:maximize" color="white" />
              </StyledIconButton>
            )}
          </>
        )}

        {startedByCurrentMember && (
          <Tooltip title="Is Taking Notes">
            <IconButton
              edge="end"
              size="small"
              disableTouchRipple
              sx={{ display: { sm: 'none' } }}
            >
              <Iconify icon="tabler:file-text" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </MemberInfo>
  );
};

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.ai[100], 0.48),
  borderRadius: theme.shape.borderRadius,
  '&:hover, &.theaterButton.theaterMember': {
    backgroundColor: alpha(theme.palette.primary.main, 0.48),
  },
}));

const StyledIcon = styled(Iconify)(({ theme }) => ({
  width: theme.spacing(2),
  height: theme.spacing(2),
}));
